"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Apple, Bone, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, Fish, ShieldCheck, Scale, Sparkles, Pill, HeartHandshake, Layers, Baby, BookOpen, ArrowUpRight, Search, Funnel as Filter } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
interface NutritionTopicItem {
  id: string;
  title: string;
  description: string;
  iconName: string;
  category: "canine" | "feline" | "safety" | "wellness" | "clinical";
  indicatorType: "safe" | "caution" | "guide" | "verified";
  indicatorText: string;
  articleCount: number;
  to: string;
}
const TOPICS_DATA: NutritionTopicItem[] = [{
  id: "dog-food",
  title: "Dog food",
  description: "Complete dry, wet, and fresh feeding standards matched to breed size, metabolic needs, and life stages.",
  iconName: "bone",
  category: "canine",
  indicatorType: "verified",
  indicatorText: "AAFCO compliant",
  articleCount: 42,
  to: "/NutritionPage"
}, {
  id: "cat-food",
  title: "Cat food",
  description: "Obligate carnivore dietary requirements focusing on moisture density, animal protein, and essential taurine.",
  iconName: "fish",
  category: "feline",
  indicatorType: "verified",
  indicatorText: "High protein",
  articleCount: 38,
  to: "/NutritionPage"
}, {
  id: "human-foods-dogs",
  title: "Human foods dogs can eat",
  description: "Verified directory of table foods, fruits, lean meats, and vegetables safe for canine digestion.",
  iconName: "apple",
  category: "canine",
  indicatorType: "safe",
  indicatorText: "Safe food database",
  articleCount: 65,
  to: "/NutritionPage"
}, {
  id: "human-foods-cats",
  title: "Human foods cats can eat",
  description: "Strict feline tolerance guide for occasional unseasoned cooked meats, plain fish, and safe fibrous bites.",
  iconName: "fish",
  category: "feline",
  indicatorType: "safe",
  indicatorText: "Feline safe list",
  articleCount: 31,
  to: "/NutritionPage"
}, {
  id: "foods-pets-avoid",
  title: "Foods pets should avoid",
  description: "Toxicology profiles detailing xylitol, alliums, chocolate, grapes, and household foods that require emergency care.",
  iconName: "alert",
  category: "safety",
  indicatorType: "caution",
  indicatorText: "Toxicity risk",
  articleCount: 54,
  to: "/PetHealthPage"
}, {
  id: "treats",
  title: "Treats",
  description: "Portion guidelines and low-calorie training rewards to keep snacks under 10% of daily caloric intake.",
  iconName: "sparkles",
  category: "wellness",
  indicatorType: "guide",
  indicatorText: "Calorie managed",
  articleCount: 29,
  to: "/NutritionPage"
}, {
  id: "supplements",
  title: "Supplements",
  description: "Clinical breakdown of omega-3 fatty acids, probiotics, and glucosamine with research-backed dosing.",
  iconName: "pill",
  category: "wellness",
  indicatorType: "verified",
  indicatorText: "Evidence based",
  articleCount: 24,
  to: "/NutritionPage"
}, {
  id: "vitamins",
  title: "Vitamins",
  description: "Nutritional role of water-soluble and fat-soluble vitamins, preventing dangerous deficiencies or toxic overdoses.",
  iconName: "pill",
  category: "wellness",
  indicatorType: "guide",
  indicatorText: "Micro-nutrients",
  articleCount: 19,
  to: "/NutritionPage"
}, {
  id: "weight-management",
  title: "Weight management",
  description: "Caloric deficit calculation, body condition scoring charts, and safe weight loss trajectories.",
  iconName: "scale",
  category: "clinical",
  indicatorType: "verified",
  indicatorText: "BCS protocols",
  articleCount: 33,
  to: "/NutritionPage"
}, {
  id: "puppy-feeding",
  title: "Puppy feeding",
  description: "Calcium-to-phosphorus ratios and growth curve feeding charts tailored for small, medium, and giant breeds.",
  iconName: "baby",
  category: "canine",
  indicatorType: "guide",
  indicatorText: "Growth support",
  articleCount: 27,
  to: "/DogsPage"
}, {
  id: "kitten-feeding",
  title: "Kitten feeding",
  description: "High-density caloric schedules, weaning transitions, and digestive support for young developing felines.",
  iconName: "baby",
  category: "feline",
  indicatorType: "guide",
  indicatorText: "High caloric density",
  articleCount: 22,
  to: "/CatsPage"
}, {
  id: "senior-pet-nutrition",
  title: "Senior pet nutrition",
  description: "Adjusting phosphorus, joint-support compounds, and easily digestible proteins for aging companions.",
  iconName: "heart",
  category: "clinical",
  indicatorType: "verified",
  indicatorText: "Geriatric care",
  articleCount: 36,
  to: "/NutritionPage"
}, {
  id: "special-diets",
  title: "Special diets",
  description: "Therapeutic renal, gastrointestinal, and hepatic nutritional plans prescribed for diagnosed conditions.",
  iconName: "layers",
  category: "clinical",
  indicatorType: "caution",
  indicatorText: "Veterinary Rx",
  articleCount: 41,
  to: "/NutritionPage"
}, {
  id: "food-allergies",
  title: "Food allergies",
  description: "Novel protein rotation, hydrolyzed diets, and 8-week elimination trial protocols to isolate food sensitivities.",
  iconName: "shield",
  category: "clinical",
  indicatorType: "caution",
  indicatorText: "Elimination trial",
  articleCount: 28,
  to: "/PetHealthPage"
}, {
  id: "ingredient-guides",
  title: "Ingredient guides",
  description: "How to read pet food guaranteed analysis labels, deciphering meals, by-products, preservatives, and binders.",
  iconName: "book",
  category: "wellness",
  indicatorType: "guide",
  indicatorText: "Label decoder",
  articleCount: 47,
  to: "/NutritionPage"
}];
const FILTER_TABS = [{
  id: "all",
  label: "All 15 topics"
}, {
  id: "canine",
  label: "Canine diets"
}, {
  id: "feline",
  label: "Feline diets"
}, {
  id: "safety",
  label: "Safety & toxicity"
}, {
  id: "clinical",
  label: "Clinical & life-stage"
}];
function renderTopicIcon(name: string) {
  const iconProps = {
    className: "size-5 text-foreground stroke-[1.75]"
  };
  switch (name) {
    case "bone":
      return <Bone {...iconProps} />;
    case "fish":
      return <Fish {...iconProps} />;
    case "apple":
      return <Apple {...iconProps} />;
    case "alert":
      return <AlertTriangle className="size-5 text-destructive stroke-[1.75]" />;
    case "sparkles":
      return <Sparkles {...iconProps} />;
    case "pill":
      return <Pill {...iconProps} />;
    case "scale":
      return <Scale {...iconProps} />;
    case "baby":
      return <Baby {...iconProps} />;
    case "heart":
      return <HeartHandshake {...iconProps} />;
    case "layers":
      return <Layers {...iconProps} />;
    case "shield":
      return <ShieldCheck {...iconProps} />;
    case "book":
      return <BookOpen {...iconProps} />;
    default:
      return <Sparkles {...iconProps} />;
  }
}
function renderIndicatorBadge(type: string, text: string) {
  switch (type) {
    case "safe":
      return <Badge variant="secondary" className="bg-card text-foreground border border-border gap-1 font-medium text-xs px-2.5 py-0.5">
          <CheckCircle2 className="size-3 text-foreground" />
          {text}
        </Badge>;
    case "caution":
      return <Badge variant="destructive" className="gap-1 font-medium text-xs px-2.5 py-0.5">
          <AlertTriangle className="size-3" />
          {text}
        </Badge>;
    case "verified":
      return <Badge variant="secondary" className="bg-muted text-muted-foreground border border-border gap-1 font-medium text-xs px-2.5 py-0.5">
          <ShieldCheck className="size-3 text-foreground" />
          {text}
        </Badge>;
    case "guide":
    default:
      return <Badge variant="outline" className="text-muted-foreground border-border gap-1 font-medium text-xs px-2.5 py-0.5">
          {text}
        </Badge>;
  }
}
export default function Nutrition() {
  const shouldReduceMotion = useReducedMotion();
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const filteredTopics = TOPICS_DATA.filter(item => {
    const matchesFilter = activeFilter === "all" || activeFilter === "canine" && item.category === "canine" || activeFilter === "feline" && item.category === "feline" || activeFilter === "safety" && item.category === "safety" || activeFilter === "clinical" && (item.category === "clinical" || item.category === "wellness");
    const matchesSearch = searchQuery.trim() === "" || item.title.toLowerCase().includes(searchQuery.toLowerCase()) || item.description.toLowerCase().includes(searchQuery.toLowerCase()) || item.indicatorText.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });
  return <section id="nutrition-topics" data-nav="light" className="bg-background text-foreground py-14 md:py-20 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted border border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
            <span className="size-2 rounded-full bg-foreground" />
            Clinical Nutrition Matrix
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground leading-tight">
            Explore pet nutrition topics
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Find evidence-informed dietary guides, safe ingredient lists, and life-stage feeding advice reviewed by veterinary professionals.
          </p>
        </div>

        {/* Clinical Governance Callout */}
        <div className="mb-10 rounded-xl bg-card border border-border border-l-4 border-l-foreground p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <div className="p-2 rounded-lg bg-background border border-border shrink-0 mt-0.5">
              <ShieldCheck className="size-5 text-foreground" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                Veterinary Nutritional Standard
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                All feeding calculations and toxicity assessments reference AAFCO, FEDIAF, and veterinary internal medicine consensus statements.
              </p>
            </div>
          </div>
          <Button asChild variant="outline" size="sm" className="shrink-0 border-border text-xs font-semibold">
            <Link to="/PetHealthPage">Review editorial criteria</Link>
          </Button>
        </div>

        {/* Filters and Search Bar */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8 pb-6 border-b border-border">
          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-2">
            {FILTER_TABS.map((tab, i) => <Button key={tab.id} data-index={i} type="button" variant={activeFilter === tab.id ? "default" : "secondary"} size="sm" onClick={() => setActiveFilter(tab.id)} className="text-xs font-medium rounded-md px-3.5 py-1.5 transition-colors">
                {tab.label}
              </Button>)}
          </div>

          {/* Search Box */}
          <div className="relative w-full lg:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search ingredient or topic..." className="w-full pl-9 pr-4 py-2 rounded-md bg-card border border-input text-xs sm:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
        </div>

        {/* 15-Item Matrix Grid */}
        {filteredTopics.length === 0 ? <div className="text-center py-16 bg-card rounded-xl border border-border p-8">
            <Filter className="size-8 mx-auto text-muted-foreground mb-3" />
            <h3 className="font-serif text-lg font-semibold text-foreground">No matching nutrition topics</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
              Try adjusting your search query or reset the category filters to browse the complete library.
            </p>
            <Button type="button" variant="outline" size="sm" onClick={() => {
          setActiveFilter("all");
          setSearchQuery("");
        }} className="mt-4 text-xs">
              Reset filters
            </Button>
          </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filteredTopics.map((topic, i) => <motion.div key={topic.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.15
        }} transition={{
          duration: 0.35,
          delay: shouldReduceMotion ? 0 : i % 3 * 0.06
        }} className="h-full flex">
                <Card className="flex flex-col justify-between w-full rounded-xl bg-card border border-border hover:border-border hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-background border border-border">
                        {renderTopicIcon(topic.iconName)}
                      </div>
                      {renderIndicatorBadge(topic.indicatorType, topic.indicatorText)}
                    </div>
                    <CardTitle className="font-serif text-xl text-foreground font-semibold tracking-tight mt-2">
                      {topic.title}
                    </CardTitle>
                    <CardDescription className="text-xs text-muted-foreground font-medium">
                      {topic.articleCount} reviewed guides
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0 flex-1 flex flex-col justify-between">
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-5">
                      {topic.description}
                    </p>

                    <div className="pt-3 border-t border-border flex items-center justify-between">
                      <span className="text-xs font-semibold text-foreground group-hover:underline">
                        Explore guide
                      </span>
                      <Button asChild variant="ghost" size="sm" className="h-8 px-2 text-foreground hover:bg-muted">
                        <Link to={topic.to} className="flex items-center gap-1 text-xs">
                          <span>Browse</span>
                          <ArrowUpRight className="size-3.5" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>}

        {/* Footer Guidance Strip */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="size-2 rounded-full bg-foreground" />
            <p className="text-xs text-muted-foreground">
              Need personalized advice for a medical condition? Always consult your veterinarian before altering clinical diets.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild variant="secondary" size="sm" className="text-xs font-semibold">
              <Link to="/PetHealthPage">Search symptoms</Link>
            </Button>
            <Button asChild variant="default" size="sm" className="text-xs font-semibold">
              <Link to="/NutritionPage">Full feeding calculator</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}